import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { FaBars, FaTimes } from "react-icons/fa";
import { FaRegSquare } from "react-icons/fa6";
import { FaShoppingCart } from "react-icons/fa";

const API_BASE_URL = "https://backend.dlapakistan.com/api";
const API_ENDPOINTS = {
  DLA_SHOP_CATEGORIES: `${API_BASE_URL}/dla-shop/categories/`,
  DLA_SHOP_BASKET_COUNT: `${API_BASE_URL}/dla-shop/basket/count/`,
};

const DlashopLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [basketCount, setBasketCount] = useState(0);

  const fetchCategories = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(API_ENDPOINTS.DLA_SHOP_CATEGORIES);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      
      let categoriesData = [];
      if (Array.isArray(data)) {
        categoriesData = data;
      } else if (data?.results) {
        categoriesData = data.results;
      } else if (data?.data) {
        categoriesData = data.data;
      }
      
      const sortedCategories = [...categoriesData].sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
      
      setCategories(sortedCategories);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError("Failed to load shop categories.");
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  const initializeSession = async () => {
    try {
      await fetch(API_ENDPOINTS.DLA_SHOP_BASKET_COUNT, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
    } catch (error) {
      console.error("Error initializing session:", error);
    }
  };

  const fetchBasketCount = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.DLA_SHOP_BASKET_COUNT, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setBasketCount(data.count || 0);
      }
    } catch (error) {
      console.error("Error fetching basket count:", error);
    }
  };

  useEffect(() => {
    const handleBasketUpdate = (event) => {
      setBasketCount(event.detail.count || 0);
    };

    window.addEventListener('basketUpdated', handleBasketUpdate);
    
    return () => {
      window.removeEventListener('basketUpdated', handleBasketUpdate);
    };
  }, []);

  useEffect(() => {
    initializeSession();
    fetchCategories();
    fetchBasketCount();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 1029) setIsSidebarOpen(false);
    };
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobileView = windowWidth <= 1029;
  const isBasketPage = () => {
    return location.pathname === "/dlashop/basket" || location.pathname === "/dlashop/checkout";
  };

  const handleBasketClick = () => {
    navigate("/dlashop/basket");
    if (isMobileView) setIsSidebarOpen(false);
  };

  const SidebarContent = () => (
    <>
      <div className="bg-[#70B444] text-white p-3 md:p-4 text-center mb-4">
        <h3 className="m-0 text-base lg:text-lg font-semibold">Browse DLA Shop</h3>
      </div>
      
      <div className="space-y-1">
        <div>
          <div onClick={() => { navigate("/dlashop"); if (isMobileView) setIsSidebarOpen(false); }}
            className={`flex items-center py-2.5 text-[#70B444] font-medium cursor-pointer transition-all hover:bg-[rgba(112,180,68,0.1)] px-3 md:px-4 text-sm lg:text-base ${
              location.pathname === "/dlashop" ? "bg-[rgba(112,180,68,0.1)] border-l-4 border-l-[#70B444]" : ""
            }`}
          >
            <FaRegSquare className="w-3.5 h-3.5 mr-3 text-[#70B444] flex-shrink-0" />
            <span className={location.pathname === "/dlashop" ? "text-[#2d5a12] font-semibold" : "text-[#70B444]"}>
              All Products
            </span>
          </div>
        </div>

        {isLoading ? (
          <div className="px-3 md:px-4 py-3">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#70B444]"></div>
              <span className="ml-2 text-sm text-gray-600">Loading categories...</span>
            </div>
          </div>
        ) : error ? (
          <div className="px-3 md:px-4 py-3">
            <div className="text-red-500 text-sm bg-red-50 p-2 rounded">{error}</div>
          </div>
        ) : categories.length === 0 ? (
          <div className="px-3 md:px-4 py-3">
            <div className="text-gray-500 text-sm italic">No shop categories yet</div>
          </div>
        ) : (
          categories.map((category) => {
            const isActive = location.pathname === `/dlashop/${category.id}`;
            const isPublished = category.is_published;
            
            return (
              <div key={`category-${category.id}`}>
                <div onClick={() => { if (isPublished) { navigate(`/dlashop/${category.id}`); if (isMobileView) setIsSidebarOpen(false); } }}
                  className={`flex items-center justify-between py-2.5 font-medium cursor-pointer transition-all hover:bg-[rgba(112,180,68,0.1)] px-3 md:px-4 text-sm lg:text-base ${
                    isActive ? "bg-[rgba(112,180,68,0.1)] border-l-4 border-l-[#70B444]" : ""
                  } ${!isPublished ? "opacity-60 cursor-not-allowed" : ""}`}
                  title={!isPublished ? "This category is not published" : ""}
                >
                  <div className="flex items-center min-w-0">
                    <FaRegSquare className={`w-3.5 h-3.5 mr-3 flex-shrink-0 ${isPublished ? "text-[#70B444]" : "text-gray-400"}`} />
                    <span className={`truncate ${isActive ? "text-[#2d5a12] font-semibold" : isPublished ? "text-[#70B444]" : "text-gray-500"}`}>
                      {category.title}
                    </span>
                    {!isPublished && <span className="ml-2 text-xs text-gray-500 italic">(Draft)</span>}
                  </div>
                  <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                    {category.products_count > 0 && (
                      <span className="text-xs bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded">
                        {category.products_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="mt-6 pt-4 border-t border-gray-200">
        <div onClick={handleBasketClick}
          className={`flex items-center justify-between py-2.5 font-medium cursor-pointer transition-all hover:bg-[rgba(112,180,68,0.1)] px-3 md:px-4 text-sm lg:text-base ${
            isBasketPage() ? "bg-[rgba(112,180,68,0.1)] border-l-4 border-l-[#70B444]" : ""
          }`}
        >
          <div className="flex items-center min-w-0">
            <FaShoppingCart className={`w-4 h-4 mr-3 flex-shrink-0 text-[#70B444]`} />
            <span className={`truncate ${isBasketPage() ? "text-[#2d5a12] font-semibold" : "text-[#70B444]"}`}>
              Shopping Basket
            </span>
          </div>
          <div className="flex items-center gap-2 ml-2 flex-shrink-0">
            {basketCount > 0 && (
              <span className="text-xs bg-[#70B444] text-white px-2 py-1 rounded-full">
                {basketCount}
              </span>
            )}
          </div>
        </div>
        <div className="px-3 md:px-4 mt-1">
          <span className="text-xs text-gray-500">View cart & checkout</span>
        </div>
      </div>
    </>
  );

  const getPageTitle = () => {
    if (location.pathname === "/dlashop/basket") return "Shopping Basket";
    if (location.pathname === "/dlashop/checkout") return "Checkout";
    if (location.pathname === "/dlashop") return "All Products";
    
    const categoryMatch = location.pathname.match(/\/dlashop\/(\d+)/);
    if (categoryMatch) {
      const categoryId = categoryMatch[1];
      const category = categories.find(cat => cat.id.toString() === categoryId);
      return category ? `${category.title} Products` : "Category Products";
    }
    
    return "DLA Shop";
  };

  const isSubPage = getPageTitle() !== "DLA Shop" && getPageTitle() !== "All Products" && getPageTitle() !== "Shopping Basket" && getPageTitle() !== "Checkout";

  return (
    <div className="w-full mx-auto px-0 py-6 mt-8 md:mt-12">
      <div className="max-w-[98vw] xl:max-w-[95vw] mx-auto px-4 xl:px-0">
        <nav className="text-gray-500 mb-8 text-sm mt-40 font-medium pt-2">
          <span onClick={() => navigate("/")} className="text-[#70B444] hover:underline cursor-pointer font-medium">Home</span>
          <span className="text-gray-400 mx-2">»</span>
          <span onClick={() => navigate("/dlashop")} className="text-black hover:text-[#70B444] hover:underline cursor-pointer font-medium uppercase tracking-wide transition-colors">
            DLA Shop
          </span>
          {isSubPage && (
            <>
              <span className="text-gray-400 mx-2">»</span>
              <span className="text-black font-medium uppercase tracking-wide">{getPageTitle()}</span>
            </>
          )}
          {location.pathname === "/dlashop/basket" && (
            <>
              <span className="text-gray-400 mx-2">»</span>
              <span className="text-black font-medium uppercase tracking-wide">Shopping Basket</span>
            </>
          )}
          {location.pathname === "/dlashop/checkout" && (
            <>
              <span className="text-gray-400 mx-2">»</span>
              <span className="text-black font-medium uppercase tracking-wide">Checkout</span>
            </>
          )}
        </nav>
      </div>

      <div className="flex w-full">
        {!isMobileView && (
          <aside className="w-72 xl:w-80 flex-shrink-0 bg-[#f7f9fc] p-4 md:p-5 rounded-lg shadow-sm h-fit sticky top-24 ml-5">
            <SidebarContent />
          </aside>
        )}

        <div className="flex-1 min-w-0 p-6 md:p-8">
          <div className="flex justify-between items-center border-b-2 border-[#70B444] pb-4 mb-8">
            <h1 className="text-2xl md:text-3xl font-extrabold text-[#2d5a12]">
              {getPageTitle()}
            </h1>
            {isMobileView && (
              <button onClick={() => setIsSidebarOpen(true)} className="flex items-center gap-2 text-[#70B444] border border-[#70B444] rounded-lg px-3 py-2 hover:bg-[#70B444] hover:text-white transition-all text-sm font-semibold shrink-0">
                <FaBars /> Menu
              </button>
            )}
          </div>
          
          <div className="min-h-[400px]">
            <Outlet />
          </div>
        </div>
      </div>

      {isMobileView && (
        <>
          <div className={`fixed top-0 right-0 h-full w-[85vw] max-w-sm bg-white z-[60] shadow-2xl transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? "translate-x-0" : "translate-x-full"}`} style={{ top: `0`, height: `100vh` }}>
            <div className="flex flex-col h-full">
              <div className="p-4 border-b flex justify-between items-center bg-[#70B444] text-white">
                <span className="font-bold text-lg">Navigation</span>
                <FaTimes className="cursor-pointer text-2xl" onClick={() => setIsSidebarOpen(false)} />
              </div>
              <div className="overflow-y-auto flex-1 p-2 bg-[#f7f9fc]">
                <SidebarContent />
              </div>
            </div>
          </div>
          {isSidebarOpen && <div className="fixed inset-0 bg-black/50 z-[50] transition-opacity" style={{ top: `0` }} onClick={() => setIsSidebarOpen(false)} />}
        </>
      )}
    </div>
  );
};

export default DlashopLayout;